/**
 * todoStore — Zustand store for Task Management with Firebase RTDB.
 * Customer Support Company ERP — NO production/QC/manufacturing.
 */
import { create } from 'zustand';
import { db } from '../../../integrations/firebase';
import { ref, set, push, update, get, remove, onValue, off } from 'firebase/database';

let _unsubscribe = null;

// Mutate a task through the proxy (HTTPS) — reliable when the client realtime
// write is blocked. Returns true on success. Throws only on explicit server error.
async function proxyMutate(taskId, body) {
  const { authHeaders } = await import('../../../core/api/authHeaders');
  const base = (import.meta.env.VITE_PROXY_URL || (import.meta.env.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');
  const res = await fetch(`${base}/api/tasks/${encodeURIComponent(taskId)}/mutate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(body),
  });
  if (res.ok) return true;
  if (res.status === 503) return false; // proxy/admin not configured → caller falls back
  const j = await res.json().catch(() => ({}));
  throw new Error(j.error || `Task update failed (HTTP ${res.status})`);
}

export const useTodoStore = create((set, getState) => ({
  tasks: [],
  isInitialized: false,
  activeTimers: {},

  // ── Initialize: Realtime listener on tasks/ ─────────────────────────────────
  initTasks: (currentUser) => {
    if (getState().isInitialized) return;
    const tasksRef = ref(db, 'tasks');
    _unsubscribe = onValue(tasksRef, (snapshot) => {
      if (!snapshot.exists()) {
        set({ tasks: [], isInitialized: true });
        return;
      }
      const data = snapshot.val();
      const tasks = Object.entries(data)
        .map(([key, val]) => ({ ...val, id: key }))
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      set({ tasks, isInitialized: true });
    }, (error) => {
      console.error('[todoStore] Tasks listener error:', error);
      set({ isInitialized: true });
    });
  },

  // ── Create Task ─────────────────────────────────────────────────────────────
  createTask: async (taskData, currentUser) => {
    // PRIMARY PATH: create via the proxy over HTTPS. This is reliable even when
    // the realtime-DB write is blocked by a browser extension / network, which
    // is the common failure mode for the direct client write below.
    try {
      const { authHeaders } = await import('../../../core/api/authHeaders');
      const base = (import.meta.env.VITE_PROXY_URL || (import.meta.env.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');
      const res = await fetch(`${base}/api/tasks/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify(taskData),
      });
      if (res.ok) {
        const j = await res.json();
        return j.id;
      }
      if (res.status !== 503) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `Task create failed (HTTP ${res.status})`);
      }
      // 503 → proxy/admin not configured; fall through to the client write.
      console.warn('[todoStore] proxy task-create unavailable (503), using client write');
    } catch (e) {
      // Network error or proxy down → fall back to the client write. A real
      // server-side validation error is rethrown.
      if (e?.message && /Task create failed|Title is required/.test(e.message)) throw e;
      console.warn('[todoStore] proxy task-create failed, falling back to client write:', e?.message);
    }

    try {
      // Task number. If the sequence write is denied, fall back to a timestamp
      // suffix rather than failing the whole task.
      let taskNumber;
      try {
        const seqRef = ref(db, 'system_sequences/task_sequence');
        const seqSnap = await get(seqRef);
        const nextNum = (seqSnap.exists() ? (seqSnap.val() || 0) : 0) + 1;
        await set(seqRef, nextNum);
        taskNumber = `TSK-${String(nextNum).padStart(6, '0')}`;
      } catch (seqErr) {
        console.warn('[todoStore] task-number sequence failed, using fallback:', seqErr?.message);
        taskNumber = `TSK-${Date.now()}`;
      }

      const newRef = push(ref(db, 'tasks'));
      const now = Date.now();
      const task = {
        ...taskData,
        id: newRef.key,
        taskNumber,
        createdAt: now,
        updatedAt: now,
      };
      // Remove undefined/null values
      const clean = Object.fromEntries(
        Object.entries(task).filter(([, v]) => v !== undefined && v !== null)
      );
      console.info('[todoStore] creating task', newRef.key, clean.title, '→', clean.assignedToName || clean.assignedTo);
      await set(newRef, clean);

      // Verify the write actually persisted. Retry a few times to absorb
      // read-after-write lag before declaring failure.
      let persisted = false;
      for (let i = 0; i < 3 && !persisted; i++) {
        try {
          const verify = await get(newRef);
          persisted = verify.exists();
        } catch (readErr) {
          console.warn('[todoStore] verify read failed:', readErr?.message);
        }
        if (!persisted) await new Promise(r => setTimeout(r, 350));
      }
      if (!persisted) {
        throw new Error('Task could not be saved to the server. This is usually an ad-blocker or privacy extension blocking the database connection — try an incognito window or disable extensions, then retry.');
      }

      // Activity log — inline + best-effort; never fails task creation.
      try {
        const aRef = push(ref(db, `task_activities/${newRef.key}`));
        await set(aRef, {
          id: aRef.key, taskId: newRef.key, type: 'general', content: 'Task created',
          actorId: currentUser?.id || 'SYSTEM', actorName: currentUser?.name || 'System',
          timestamp: Date.now(),
        });
      } catch (logErr) {
        console.warn('[todoStore] activity log skipped:', logErr?.message);
      }

      return newRef.key;
    } catch (e) {
      console.error('[todoStore] Failed to create task:', e?.code, e?.message, e);
      throw e;
    }
  },

  // ── Update Task ─────────────────────────────────────────────────────────────
  updateTask: async (taskId, updates) => {
    // PRIMARY: proxy over HTTPS (reliable). Falls back to the client write.
    try {
      const ok = await proxyMutate(taskId, { updates });
      if (ok) return;
    } catch (e) {
      if (/Task update failed/.test(e?.message || '')) throw e;
      console.warn('[todoStore] proxy update failed, client fallback:', e?.message);
    }
    try {
      const clean = Object.fromEntries(
        Object.entries({ ...updates, updatedAt: Date.now() })
          .filter(([, v]) => v !== undefined)
      );
      await update(ref(db, `tasks/${taskId}`), clean);
    } catch (e) {
      console.error('[todoStore] Failed to update task:', e);
      throw e;
    }
  },

  // ── Delete Task ─────────────────────────────────────────────────────────────
  deleteTask: async (taskId) => {
    try {
      await remove(ref(db, `tasks/${taskId}`));
      await remove(ref(db, `task_activities/${taskId}`));
    } catch (e) {
      console.error('[todoStore] Failed to delete task:', e);
      throw e;
    }
  },

  // ── Toggle Status ───────────────────────────────────────────────────────────
  toggleTaskStatus: async (taskId, newStatus, currentUser) => {
    const now = Date.now();
    const updates = { status: newStatus, statusChangedAt: now };
    if (newStatus === 'completed') updates.completedAt = now;
    if (newStatus === 'in_progress') updates.startedAt = now;
    await getState().updateTask(taskId, updates);
    try {
      const lf = getState().logActivity;
      if (typeof lf === 'function') {
        await lf(taskId, 'status_change', `Status changed to ${newStatus.replace('_', ' ')}`, currentUser);
      }
    } catch (e) { console.warn('[todoStore] status activity log skipped:', e?.message); }
  },

  // ── Subtasks (embedded in task) ─────────────────────────────────────────────
  addSubtask: async (taskId, title, currentUser) => {
    const task = (getState().tasks || []).find(t => t.id === taskId);
    if (!task) return;
    const newSubtask = {
      id: `sub-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 4)}`,
      taskId,
      title,
      completed: false,
      createdAt: Date.now(),
      createdBy: currentUser?.id || 'SYSTEM',
    };
    const existing = task.subtasks || [];
    const updated = [...existing, newSubtask];
    await getState().updateTask(taskId, { subtasks: updated });
    await getState().logActivity(taskId, 'subtask_added', `Subtask added: "${title}"`, currentUser);
  },

  toggleSubtask: async (taskId, subtaskId, completed, currentUser) => {
    const task = (getState().tasks || []).find(t => t.id === taskId);
    if (!task) return;
    const updated = (task.subtasks || []).map(s =>
      s.id === subtaskId
        ? {
            ...s,
            completed,
            completedAt: completed ? Date.now() : null,
            completedBy: completed ? (currentUser?.id || 'SYSTEM') : null,
          }
        : s
    );
    await getState().updateTask(taskId, { subtasks: updated });
    if (completed) {
      const sub = updated.find(s => s.id === subtaskId);
      await getState().logActivity(taskId, 'subtask_completed', `Subtask completed: "${sub?.title || subtaskId}"`, currentUser);
    }
  },

  deleteSubtask: async (taskId, subtaskId) => {
    const task = (getState().tasks || []).find(t => t.id === taskId);
    if (!task) return;
    const updated = (task.subtasks || []).filter(s => s.id !== subtaskId);
    await getState().updateTask(taskId, { subtasks: updated });
  },

  // ── Activity / Comments ─────────────────────────────────────────────────────
  logActivity: async (taskId, type, content, currentUser) => {
    const activity = {
      type, content,
      actorId: currentUser?.id || currentUser?.uid || 'SYSTEM',
      actorName: currentUser?.name || currentUser?.displayName || 'System',
    };
    // PRIMARY: proxy (reliable). Comment count bumped via updateTask (also proxy).
    try {
      const ok = await proxyMutate(taskId, { activity });
      if (ok) {
        if (type === 'comment') {
          const task = (getState().tasks || []).find(t => t.id === taskId);
          await getState().updateTask(taskId, { commentCount: ((task?.commentCount) || 0) + 1 });
        }
        return;
      }
    } catch (e) {
      console.warn('[todoStore] proxy activity failed, client fallback:', e?.message);
    }
    // Fallback: client write (best-effort).
    try {
      const actRef = push(ref(db, `task_activities/${taskId}`));
      await set(actRef, { id: actRef.key, taskId, ...activity, timestamp: Date.now() });
      if (type === 'comment') {
        const task = (getState().tasks || []).find(t => t.id === taskId);
        await update(ref(db, `tasks/${taskId}`), { commentCount: ((task?.commentCount) || 0) + 1, updatedAt: Date.now() });
      }
    } catch (e) {
      console.error('[todoStore] Failed to log activity:', e);
    }
  },

  getActivities: async (taskId) => {
    try {
      const snap = await get(ref(db, `task_activities/${taskId}`));
      if (!snap.exists()) return [];
      const data = snap.val();
      return Object.entries(data)
        .map(([key, val]) => ({ ...val, id: key }))
        .sort((a, b) => b.timestamp - a.timestamp);
    } catch (e) {
      console.error('[todoStore] Failed to get activities:', e);
      return [];
    }
  },

  // ── Timer (local-first, logs on stop) ───────────────────────────────────────
  startTimer: (taskId) => {
    set(state => ({
      activeTimers: { ...state.activeTimers, [taskId]: { startTime: Date.now() } },
    }));
  },

  stopTimer: async (taskId, currentUser) => {
    const timer = getState().activeTimers[taskId];
    if (!timer) return;
    const durationMs = Date.now() - timer.startTime;
    const durationMinutes = Math.round(durationMs / 60000);

    // Log as activity
    await getState().logActivity(
      taskId, 'timer_stopped',
      `Timer stopped — ${durationMinutes} min logged`,
      currentUser
    );

    // Update cached total on task (via proxy-backed updateTask)
    const task = (getState().tasks || []).find(t => t.id === taskId);
    const prev = task?.timeLogMinutes || 0;
    await getState().updateTask(taskId, { timeLogMinutes: prev + durationMinutes });

    set(state => {
      const timers = { ...state.activeTimers };
      delete timers[taskId];
      return { activeTimers: timers };
    });
  },

  // ── Cleanup ─────────────────────────────────────────────────────────────────
  cleanup: () => {
    if (_unsubscribe) {
      _unsubscribe();
      _unsubscribe = null;
    }
    set({ tasks: [], isInitialized: false, activeTimers: {} });
  },
}));
